/**
 * The option lists behind the report filter drawers — one loader per list, replacing five
 * copies of fetchPortalFilters and three near-identical fetch*FilterOptions
 * (docs/DUPLICATION_AUDIT.md, B14).
 */

import { apiFetch, postReport } from '../services/api.js';
import { fetchReasonCodeOptions } from './reasonCodeOptions.js';
import { NURSE_CALL_CATEGORY_FILTER } from './deviceCategories.js';
import { convertSecto12Hour, luxonParseSQLDate, luxonCompareDateTimes } from '../libs.jsx';
import { fetchCheckinWindows, fetchRoundsWindows } from '../pages/settings/wellness-settings/api/wellnessSettingsApi.js';

/* group → [request flag, response key]. getportalfilters only checks each flag for
   presence (any non-empty string includes the group), which is why the old copies' '0'
   and '1' for area_filter behaved the same. */
const PORTAL_GROUPS = {
  areas: ['area_filter', 'areas'],
  areaGroups: ['area_group_filter', 'area_groups'],
  areaTypes: ['area_type_filter', 'area_types'],
  residents: ['resident_filter', 'residents'],
  residentGroups: ['resident_group_filter', 'resident_groups'],
  users: ['user_filter', 'users'],
  userGroups: ['user_group_filter', 'user_groups'],
  deviceCategories: ['device_category_filter', 'device_categories'],
};
export const ALL_PORTAL_GROUPS = Object.keys(PORTAL_GROUPS);

/**
 * Areas, groups, residents, users and device categories for a campus (getportalfilters).
 * Residents gain `resident_name` (first + last), which the drawers display.
 *
 * @param {number} campusId
 * @param {string[]} [groups] — which lists to load (default: all)
 * @returns {Promise<object>} one array per requested group; {} if the request fails
 */
export async function fetchPortalFilters(campusId, groups = ALL_PORTAL_GROUPS) {
  const body = { rep_type: 'getportalfilters', campus_id: campusId };
  Object.entries(PORTAL_GROUPS).forEach(([group, [flag]]) => { body[flag] = groups.includes(group) ? '1' : ''; });
  const res = await postReport(body, '[reportFilterOptions] fetchPortalFilters error');
  if (res.ErrorCode) return {};
  const out = {};
  groups.forEach((group) => { out[group] = res.data?.[PORTAL_GROUPS[group][1]] || []; });
  if (out.residents) {
    out.residents = out.residents.map((r) => ({
      ...r,
      resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
    }));
  }
  return out;
}

/**
 * Event types, sorted by name: att_event_types comes back in table order and is the
 * longest list in the drawer (FilterSubSection renders options as received). Executive
 * and Environmental did not sort.
 */
export async function fetchEventTypeOptions(campusId) {
  const rows = await apiFetch({ table: 'att_event_types', procedure: 'fetchall', reqType: 'direct', pKey: 'campus_id', pVal: campusId });
  return [...(rows || [])].sort((a, b) =>
    (a.event_type_name || '').localeCompare(b.event_type_name || '', undefined, { sensitivity: 'base' }));
}

/** A window as a filter option: its times as the subtext, and "Inactive" while it is ignored
    (the report leaves ignored windows out, so a schedule on one stays empty until it's
    re-enabled). Same Active / Inactive test as the Wellness Settings tables. */
function windowOption(w) {
  const times = `${convertSecto12Hour(w.window_start)} – ${convertSecto12Hour(w.window_end)}`;
  const active = luxonCompareDateTimes(luxonParseSQLDate(w.ignore_until), 'lt');
  return { ...w, window_times: active ? times : `${times} · Inactive` };
}

/**
 * Check-in and rounds windows for the report windows filter (docs/CHECKIN_WINDOW_FILTER_PLAN.md):
 * `checkinWindows` / `roundsWindows` feed each report's drawer, and `windows` (both) lets a
 * stored schedule's chips name either type. Each list is sorted by start time.
 *
 * @returns {Promise<{ checkinWindows: object[], roundsWindows: object[], windows: object[] }>}
 */
export async function fetchWindowOptions(campusId) {
  const [checkin, rounds] = await Promise.all([
    fetchCheckinWindows(campusId).catch(() => []),
    fetchRoundsWindows(campusId).catch(() => []),
  ]);
  const prep = (rows) => (Array.isArray(rows) ? rows : [])
    .map(windowOption)
    .sort((a, b) => (a.window_start ?? 0) - (b.window_start ?? 0));
  const checkinWindows = prep(checkin);
  const roundsWindows = prep(rounds);
  return { checkinWindows, roundsWindows, windows: [...checkinWindows, ...roundsWindows] };
}

/** Nurse-call devices for the Devices filter (the categories the report procs cover). */
export async function fetchDeviceOptions(campusId) {
  const rows = await apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: { device_filter: '', device_category_filter: NURSE_CALL_CATEGORY_FILTER, campus_id: campusId },
  });
  return rows || [];
}

/**
 * The full option set — Alert Details, Hourly, Executive, Caregiver, Shift, Maintenance and
 * the scheduled-report editor. Reason codes include the synthetic "No Reason" row.
 *
 * @returns {Promise<object>} {} if any request throws
 */
export async function fetchFilterOptions(campusId) {
  try {
    const [portal, eventTypes, reasonCodes, devices] = await Promise.all([
      fetchPortalFilters(campusId),
      fetchEventTypeOptions(campusId),
      fetchReasonCodeOptions(campusId),
      fetchDeviceOptions(campusId),
    ]);
    return {
      areas: portal.areas || [],
      areaGroups: portal.areaGroups || [],
      areaTypes: portal.areaTypes || [],
      residents: portal.residents || [],
      residentGroups: portal.residentGroups || [],
      users: portal.users || [],
      userGroups: portal.userGroups || [],
      deviceCategories: portal.deviceCategories || [],
      devices,
      eventTypes,
      reasonCodes,
    };
  } catch (err) {
    console.error('[reportFilterOptions] fetchFilterOptions error:', err);
    return {};
  }
}
